import { EditNote, Settings } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { CustomDataGrid, StackedTextCell } from '@retail/components';
import { AssortmentConfig, DataOwnerConfig, LocationConfig } from '@retail/member-config/types';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { Chip } from '@shared/components';
import { CheckIcon } from '@shared/custom-icons';

const baseColumnProps: Partial<GridColDef> = {
  width: 160,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};
interface Props {
  configuration: DataOwnerConfig[];
  isLoading: boolean;
  setAssortmentsForEdit: (assortments: AssortmentConfig[]) => void;
  setMemberForAssortmentConnection: (memberConfig?: DataOwnerConfig) => void;
}

interface AssortmentTableConfig extends AssortmentConfig {
  memberName: string;
  memberId: number;
  locationLength: number;
  assortmentLength: number;
}

export function MemberConfigTable({
  configuration,
  isLoading,
  setAssortmentsForEdit,
  setMemberForAssortmentConnection,
}: Props) {
  const t = useMyAssortmentTFunction();

  const assortments: AssortmentTableConfig[] = configuration.flatMap((member) => [
    ...member.assortments.map((assortment) => {
      return {
        memberName: member.name,
        memberId: member.id,
        locationLength: member.locations.length,
        assortmentLength: member.assortments.length,
        ...assortment,
      };
    }),
  ]);

  const renderGroupHeader = (row: AssortmentTableConfig) => (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ width: '100%', px: 2 }}
    >
      <Stack>
        <Typography variant="subtitle1" color="textPrimary">
          {row.memberName}
        </Typography>
        <Typography variant="body2">{`${row.assortmentLength} sortimenter · ${row.locationLength} butikker`}</Typography>
      </Stack>
      <Stack
        flexDirection="row"
        sx={{
          position: 'sticky',
          right: 0,
          px: 2,
          backgroundColor: 'grey.A100',
          zIndex: 1,
        }}
      >
        {row.locationLength > 1 && (
          <Tooltip
            onClick={() =>
              setMemberForAssortmentConnection(
                configuration.find((member) => member.id === row.memberId)
              )
            }
            title={t('myAssortment.memberConfig.assortmentConnect')}
          >
            <IconButton size="small">
              <Settings />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('myAssortment.memberConfig.editAssortmentValues')}>
          <IconButton
            size="small"
            onClick={() =>
              setAssortmentsForEdit(
                assortments.filter((assortment) => assortment.memberName === row.memberName)
              )
            }
          >
            <EditNote />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );

  const columns: GridColDef[] = [
    {
      field: 'memberName',
      width: 40,
      headerName: '',
    },
    {
      ...baseColumnProps,
      field: 'assortmentName',
      width: 240,
      headerName: t('myAssortment.memberConfig.assortmentName').toUpperCase(),
      renderCell: ({ row, rowNode }) => (rowNode.type === 'group' ? null : row.title),
    },
    {
      ...baseColumnProps,
      field: 'locations',
      width: 240,
      headerName: t('myAssortment.memberConfig.locations').toUpperCase(),
      renderCell: ({ row, rowNode }) => {
        if (rowNode.type === 'group') {
          return null;
        } else {
          return (
            <Stack flexDirection="row" flexWrap="wrap" gap={0.5} sx={{ py: 1, width: '100%' }}>
              {row.locations.map((location: LocationConfig) => (
                <Chip
                  key={location.id}
                  size="small"
                  rounded
                  backgroundColor="inherit"
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                  label={location.name}
                />
              ))}
            </Stack>
          );
        }
      },
    },
    {
      ...baseColumnProps,
      field: 'salesPriceNotification',
      width: 105,
      renderHeader: () => (
        <Tooltip title={t('myAssortment.memberConfig.publishPriceDesc')}>
          <Typography variant="subtitle1" whiteSpace="wrap">
            {t('myAssortment.memberConfig.publishPrice').toUpperCase()}
          </Typography>
        </Tooltip>
      ),
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : row.sendSalesPriceUpdated ? <CheckIcon /> : '-',
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilToSftp',
      width: 90,
      headerName: 'SFTP',
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : row.totalfilToSftp ? <CheckIcon /> : '-',
    },
    {
      ...baseColumnProps,
      field: 'erp',
      headerName: 'ERP',
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : (
          <StackedTextCell topLine={row.erpName} bottomLine={row.erpId} />
        ),
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilFormat',
      headerName: t('myAssortment.memberConfig.fileFormat').toUpperCase(),
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : row.totalfilFormat ?? '-',
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilCharset',
      headerName: t('myAssortment.memberConfig.charset').toUpperCase(),
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : row.totalfilCharset ?? '-',
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfileVilmaVersion',
      headerName: t('myAssortment.memberConfig.vilmaVersion').toUpperCase(),
      renderCell: ({ row, rowNode }) => {
        if (rowNode.type === 'group') {
          return null;
        }
        const vilmaVersion = row.totalfilVilmaVersion;
        return vilmaVersion === 'VILMA_27'
          ? t('myAssortment.memberConfig.vilma_27')
          : vilmaVersion === 'VILMA_28'
          ? t('myAssortment.memberConfig.vilma_28')
          : '-';
      },
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfileFutureDays',
      headerName: t('myAssortment.memberConfig.futureDays').toUpperCase(),
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : row.totalfilNumberOfFutureDays ?? '-',
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilSftpDirectory',
      headerName: t('myAssortment.memberConfig.sftpDirectory').toUpperCase(),
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : row.totalfilSftpDirectory ?? '-',
    },
    {
      ...baseColumnProps,
      headerAlign: 'center',
      align: 'center',
      field: 'totalfilSftpFrequency',
      headerName: t('myAssortment.memberConfig.sftpFrequency').toUpperCase(),
      renderCell: ({ row, rowNode }) =>
        rowNode.type === 'group' ? null : row.totalfilSftpFrequency ?? '-',
    },
  ];

  return (
    <CustomDataGrid
      rows={assortments}
      loading={isLoading}
      columns={columns}
      getRowId={(row) => row.id}
      hideFooter
      disableRowSelectionOnClick
      disableColumnMenu
      columnHeaderHeight={80}
      getRowHeight={() => 'auto'}
      initialState={{
        rowGrouping: { model: ['memberName'] },
        columns: { columnVisibilityModel: { memberName: false } },
      }}
      defaultGroupingExpansionDepth={-1}
      groupingColDef={{
        hideDescendantCount: true,
        colSpan: (value, _row, _column, api) =>
          value != null ? api.current.getVisibleColumns().length : 1,
        renderCell: ({ rowNode, api }) => {
          if (rowNode.type !== 'group') {
            return null;
          }
          const row = api.getRow<AssortmentTableConfig>(rowNode.children?.[0]);
          return row ? renderGroupHeader(row) : null;
        },
      }}
      getRowClassName={(params) => {
        if (params.id.toString().startsWith('auto-generated-row-memberName')) {
          return 'grouped-header-row';
        }
        return 'standard-data-row';
      }}
      sx={{
        '& .grouped-header-row': {
          backgroundColor: (theme) => `${theme.vars.palette.grey.A100} !important`,
        },
        '& .grouped-header-row .MuiDataGrid-cell': {
          overflow: 'visible',
        },
        '& .MuiDataGrid-cell': {
          minHeight: '68px !important',
          display: 'flex',
          alignItems: 'center',
        },
      }}
      disableVirtualization
      slots={{
        groupingCriteriaExpandIcon: () => null,
        groupingCriteriaCollapseIcon: () => null,
      }}
    />
  );
}
